import React, { useState } from 'react';
import { isUnsentToSupervisor } from './SentSupervisorBadge';

// Alerta na Central de Avisos (Home) para Labor Bills ainda NÃO enviados ao supervisor.
// - PM: vê apenas os seus (laborBills já vem escopado pelo backend).
// - Admin: vê de todos, agrupado por PM (pm_user).
// Definição de "não enviado": bill sem NENHUM histórico de envio (nunca enviado ao supervisor
// nem por e-mail). Bills já enviados no passado (legado) não alarmam. Independe da data.
// Clicar num bill abre o editor; botão leva à aba Labor.
export const UnsentBillsAlert = ({ bills, user, onOpenBill, onGoToLabor, onMarkAllSent, onReconcile, language = 'pt-BR' }) => {
  const pt = language === 'pt-BR';
  const isAdmin = user?.role === 'admin';
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const unsent = (bills || []).filter((b) => b && !b.deleted && isUnsentToSupervisor(b));
  if (unsent.length === 0) return null;

  const labelOf = (b) =>
    `${b.job_name || (pt ? 'Sem nome' : 'No name')}${b.order_number ? ` · #${b.order_number}` : ''}`;

  // Agrupa por PM (apenas admin)
  const groups = {};
  if (isAdmin) {
    unsent.forEach((b) => {
      const key = b.pm_user || b.team || (pt ? 'Sem PM' : 'No PM');
      (groups[key] = groups[key] || []).push(b);
    });
  }

  const Chip = ({ b }) => (
    <button
      type="button"
      data-testid={`unsent-bill-chip-${b.id}`}
      onClick={(e) => { e.stopPropagation(); onOpenBill && onOpenBill(b.id); }}
      className="text-[11px] bg-rose-100 hover:bg-rose-200 text-rose-800 rounded px-2 py-0.5 transition-colors max-w-full truncate"
      title={labelOf(b)}
    >
      {labelOf(b)}
    </button>
  );

  return (
    <div
      data-testid="unsent-supervisor-alert"
      onClick={(e) => e.stopPropagation()}
      className="mb-4 rounded-lg border border-rose-300 bg-rose-50 p-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg" aria-hidden="true">⚠️</span>
            <h5 className="font-bold text-rose-800 text-base">
              {unsent.length}{' '}
              {pt
                ? unsent.length === 1
                  ? 'Labor Bill não enviado ao supervisor'
                  : 'Labor Bills não enviados ao supervisor'
                : unsent.length === 1
                ? 'Labor Bill not sent to supervisor'
                : 'Labor Bills not sent to supervisor'}
            </h5>
          </div>
          <p className="text-rose-700 text-xs mb-2">
            {pt
              ? 'Estes bills ainda não foram enviados ao supervisor.'
              : 'These bills have not been sent to the supervisor yet.'}
          </p>

          {isAdmin ? (
            <div className="space-y-2 max-h-56 overflow-auto pr-1">
              {Object.entries(groups).map(([pm, list]) => (
                <div key={pm} className="bg-white/70 border border-rose-200 rounded p-2">
                  <div className="text-xs font-semibold text-rose-800 mb-1">
                    👤 {pm} · {list.length}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {list.map((b) => (
                      <Chip key={b.id} b={b} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-1 max-h-40 overflow-auto pr-1">
              {unsent.map((b) => (
                <Chip key={b.id} b={b} />
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 flex flex-col gap-2 items-end">
          <button
            type="button"
            data-testid="unsent-alert-go-labor-btn"
            onClick={() => onGoToLabor && onGoToLabor()}
            className="text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white rounded px-3 py-1.5 transition-colors whitespace-nowrap"
          >
            {pt ? 'Ver na aba Labor' : 'View in Labor'}
          </button>

          {isAdmin && (
            <button
              type="button"
              data-testid="unsent-alert-reconcile-btn"
              disabled={reconciling}
              onClick={async () => {
                if (!onReconcile) return;
                setReconciling(true);
                try {
                  await onReconcile();
                } finally {
                  setReconciling(false);
                }
              }}
              className="text-xs font-semibold bg-white hover:bg-blue-50 text-blue-700 border border-blue-300 disabled:opacity-60 rounded px-3 py-1.5 transition-colors whitespace-nowrap"
              title={pt ? 'Identificar bills já enviados no passado a partir do histórico de envios' : 'Detect already-sent bills from send history'}
            >
              {reconciling ? (pt ? 'Reconciliando…' : 'Reconciling…') : `🔄 ${pt ? 'Reconciliar enviados' : 'Reconcile sent'}`}
            </button>
          )}

          {isAdmin && (
            confirming ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  data-testid="unsent-alert-mark-confirm-btn"
                  disabled={busy}
                  onClick={async () => {
                    if (!onMarkAllSent) return;
                    setBusy(true);
                    try {
                      await onMarkAllSent();
                    } finally {
                      setBusy(false);
                      setConfirming(false);
                    }
                  }}
                  className="text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded px-3 py-1.5 transition-colors whitespace-nowrap"
                >
                  {busy ? (pt ? 'Marcando…' : 'Marking…') : (pt ? 'Confirmar' : 'Confirm')}
                </button>
                <button
                  type="button"
                  data-testid="unsent-alert-mark-cancel-btn"
                  disabled={busy}
                  onClick={() => setConfirming(false)}
                  className="text-xs font-semibold bg-white hover:bg-rose-50 text-rose-700 border border-rose-300 rounded px-3 py-1.5 transition-colors whitespace-nowrap"
                >
                  {pt ? 'Cancelar' : 'Cancel'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                data-testid="unsent-alert-mark-all-btn"
                onClick={() => setConfirming(true)}
                className="text-xs font-semibold bg-white hover:bg-emerald-50 text-emerald-700 border border-emerald-300 rounded px-3 py-1.5 transition-colors whitespace-nowrap"
                title={pt ? 'Marcar o backlog atual como já enviado ao supervisor' : 'Mark current backlog as already sent'}
              >
                ✓ {pt ? 'Marcar todos como enviados' : 'Mark all as sent'}
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default UnsentBillsAlert;
